/**
 * Preview entity builders for the draft dispatcher.
 *
 * Each `build*PreviewEntity` / `build*DraftPreview` returns an
 * `EntityPreview` suitable for the scene renderer while the user is
 * still placing points for a command (LINE, CIRCLE, ARC, POLYLINE,
 * RECTANGLE, MTEXT, BLOCK, ARRAY, HATCH, DIMENSION, MULTILEADER,
 * REVCLOUD, VIEWPORT).
 *
 * Extracted from `draftingHelpers.ts` to keep the file under the
 * 2000-line hard limit set in CLAUDE.md. External code should keep
 * calling `buildDraftPreviewEntity` from `draftingHelpers.ts` —
 * the per-primitive functions are internal implementation details.
 *
 * ## Function dependency map
 *
 * buildLinePreviewEntity -> buildSimpleGeometryPreviewEntity
 * buildCirclePreviewEntity -> buildSimpleGeometryPreviewEntity,
 *                             distanceBetween
 * buildArcPreviewEntity -> resolveArcGeometry,
 *                          buildSimpleGeometryPreviewEntity,
 *                          buildLinePreviewEntity (fallback)
 * buildPolylinePreviewEntity -> createPreviewEntity, formatPointList
 * buildRectanglePreviewEntity -> rectangleCorners,
 *                                buildPolylinePreviewEntity
 * buildMTextPreviewEntity -> createPreviewEntity, formatNumber
 * buildBlockPreviewEntity -> createPreviewEntity, formatNumber
 * buildArrayRectDraftPreview -> createPreviewEntity, formatNumber,
 *                               DEFAULT_ARRAY_CELL_*
 * buildHatchPreviewEntity -> rectangleCorners, createPreviewEntity,
 *                            formatPointList
 * buildDimensionPreviewEntity -> createPreviewEntity, formatNumber,
 *                                distanceBetween
 * buildMultileaderPreviewEntity -> createPreviewEntity, formatNumber
 * buildRevisionCloudPreviewEntity -> createPreviewEntity, formatNumber
 * buildViewportPreviewEntity -> createPreviewEntity, formatNumber
 */

import {
  buildSimpleGeometryPreviewEntity,
  buildEllipsePreviewEntity as buildEllipsePreviewBase,
  type SimpleArcEntity,
  type SimpleCircleEntity,
  type SimpleEllipseEntity,
  type SimpleLineEntity,
} from "@dxf-ai-editor/command-engine";
import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { t } from "../i18n";
import {
  DEFAULT_ARRAY_CELL_HEIGHT,
  DEFAULT_ARRAY_CELL_WIDTH,
} from "../state/arrayReducers";
import {
  createPreviewEntity,
  formatNumber,
  formatPointList,
  rectangleCorners,
  resolveArcGeometry,
} from "./draftSharedHelpers";
import type { DraftPoint } from "./draftTypes";
import { distanceBetween } from "./draftTypes";

// ── Editable property lists ─────────────────────────────────────────

const sharedPreviewProperties = [
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Transparency",
  "Annotative",
] as const;

const polylineEditableProperties = [
  ...sharedPreviewProperties,
  "Constant width",
  "Closed",
  "Vertices",
] as const;

const mtextEditableProperties = [
  ...sharedPreviewProperties,
  "Contents",
  "Text style",
  "Height",
  "Width",
  "Background fill",
  "Corner 1 X",
  "Corner 1 Y",
  "Corner 2 X",
  "Corner 2 Y",
] as const;

const blockEditableProperties = [
  ...sharedPreviewProperties,
  "Name",
  "Insertion X",
  "Insertion Y",
  "Scale",
  "Rotation",
] as const;

const arrayEditableProperties = [
  ...sharedPreviewProperties,
  "Rows",
  "Columns",
  "Row spacing",
  "Column spacing",
  "Base X",
  "Base Y",
  "Axis angle",
  "Associative",
] as const;

const hatchEditableProperties = [
  ...sharedPreviewProperties,
  "Pattern",
  "Scale",
  "Associative",
  "Boundary",
] as const;

const dimensionEditableProperties = [
  ...sharedPreviewProperties,
  "Dimension style",
  "Measurement",
  "Text override",
  "Extension 1 X",
  "Extension 1 Y",
  "Extension 2 X",
  "Extension 2 Y",
  "Dimension line X",
  "Dimension line Y",
] as const;

const multileaderEditableProperties = [
  ...sharedPreviewProperties,
  "Multileader style",
  "Attachment",
  "Arrow X",
  "Arrow Y",
  "Landing X",
  "Landing Y",
  "Contents",
] as const;

const revisionCloudEditableProperties = [
  ...sharedPreviewProperties,
  "Cloud style",
  "Approximate arc length",
  "Corner 1 X",
  "Corner 1 Y",
  "Corner 2 X",
  "Corner 2 Y",
] as const;

// WIPEOUT preview properties — frame visibility flag plus the four
// rectangular-mode corners (matches the geometryReducer's expansion of
// {corner1, corner2} → 4 vertices). Layer/Color picked up from the
// shared list. Polygon-pick mode (deferred) will surface a vertex-list
// summary instead.
const wipeoutEditableProperties = [
  ...sharedPreviewProperties,
  "Frame",
  "Vertex 1 X",
  "Vertex 1 Y",
  "Vertex 2 X",
  "Vertex 2 Y",
  "Vertex 3 X",
  "Vertex 3 Y",
  "Vertex 4 X",
  "Vertex 4 Y",
] as const;

const viewportEditableProperties = [
  "Display lock",
  "Scale",
  "Layer",
  "Shade plot",
  "Corner 1 X",
  "Corner 1 Y",
  "Corner 2 X",
  "Corner 2 Y",
] as const;

// ── Preview entity builders ─────────────────────────────────────────

export function buildLinePreviewEntity(
  id: string,
  spaceId: string,
  start: DraftPoint,
  end: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  const entity: SimpleLineEntity = {
    id,
    type: "Line",
    spaceId,
    start,
    end,
  };

  return buildSimpleGeometryPreviewEntity(entity, {
    label: t("entity.label.line", { id }),
    summary,
    properties: defaultObjectProperties,
  });
}

export function buildCirclePreviewEntity(
  id: string,
  spaceId: string,
  center: DraftPoint,
  radiusPoint: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  const entity: SimpleCircleEntity = {
    id,
    type: "Circle",
    spaceId,
    center,
    radius: Math.max(1, distanceBetween(center, radiusPoint)),
  };

  return buildSimpleGeometryPreviewEntity(entity, {
    label: t("entity.label.circle", { id }),
    summary,
    properties: defaultObjectProperties,
  });
}

export function buildArcPreviewEntity(
  id: string,
  spaceId: string,
  points: DraftPoint[],
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  const arcGeometry = resolveArcGeometry(points);

  if (!arcGeometry) {
    return buildLinePreviewEntity(
      id,
      spaceId,
      points[0] ?? { x: 0, y: 0 },
      points.at(-1) ?? { x: 0, y: 0 },
      defaultObjectProperties,
      summary,
    );
  }

  const entity: SimpleArcEntity = {
    id,
    type: "Arc",
    spaceId,
    center: arcGeometry.center,
    radius: arcGeometry.radius,
    startAngle: arcGeometry.startAngle,
    endAngle: arcGeometry.endAngle,
  };

  return buildSimpleGeometryPreviewEntity(entity, {
    label: t("entity.label.arc", { id }),
    summary,
    properties: defaultObjectProperties,
  });
}

/**
 * Build the rubber-band preview for an ELLIPSE in mid-creation.
 *
 * Two modes:
 *   - majorEnd hover (after center clicked, before major-end clicked):
 *     show a temporary "circle" approximation with radius=|center-hover|
 *     so the user sees the major-axis distance until they finalize the
 *     direction. We show a thin line for clarity instead — matches
 *     AutoCAD where only an axis tracker line shows during this stage.
 *   - minorDistance hover (after major-end clicked):
 *     show the actual ellipse with current ratio = |center-hover| /
 *     |center-majorEnd|. AutoCAD uses ratio=cursor-perpendicular-distance
 *     / |majorEnd-center|; we approximate by taking the hover distance
 *     directly (the perpendicular projection is implicit because the
 *     state machine commits ratio = minorDistance / majorLength).
 */
export function buildEllipsePreviewEntity(
  id: string,
  spaceId: string,
  center: DraftPoint,
  majorEnd: DraftPoint,
  minorDistance: number,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  const majorVx = majorEnd.x - center.x;
  const majorVy = majorEnd.y - center.y;
  const majorLen = Math.hypot(majorVx, majorVy);
  const minorLen = Math.max(minorDistance, 1e-9);
  let majorAxisEndpoint = { x: majorVx, y: majorVy };
  let ratio: number;
  if (majorLen <= 1e-9) {
    majorAxisEndpoint = { x: 1, y: 0 };
    ratio = Math.min(1, minorLen);
  } else if (minorLen > majorLen) {
    majorAxisEndpoint = {
      x: -majorVy * (minorLen / majorLen),
      y: majorVx * (minorLen / majorLen),
    };
    ratio = majorLen / minorLen;
  } else {
    ratio = minorLen / majorLen;
  }
  const entity: SimpleEllipseEntity = {
    id,
    type: "Ellipse",
    spaceId,
    center: { x: center.x, y: center.y },
    majorAxisEndpoint,
    ratio,
    startParam: 0,
    endParam: Math.PI * 2,
  };
  return buildEllipsePreviewBase(entity, {
    label: t("entity.label.ellipse", { id }),
    summary,
    properties: defaultObjectProperties,
  });
}

export function buildPolylinePreviewEntity(
  id: string,
  spaceId: string,
  points: DraftPoint[],
  closed: boolean,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  return createPreviewEntity({
    id,
    label: t("entity.label.polyline", { id }),
    type: "LWPOLYLINE",
    spaceId,
    summary,
    editableProperties: polylineEditableProperties,
    properties: {
      ...defaultObjectProperties,
      "Constant width": "0",
      // DXF / adapter convention is `"true"` / `"false"` (lowercase).
      // `lwpolylineToRenderable` parses this property by comparing the
      // lowercased trimmed string to `"true"`. Writing `"Yes"` here used
      // to leave `closed=false` in the renderable geometry — the manual
      // closing-vertex append in `buildRectanglePreviewEntity` papered
      // over the bug for the polyline path, but other consumers that
      // rely on the `closed` flag (e.g. batched render closing edge)
      // saw the wrong value.
      Closed: closed ? "true" : "false",
      Vertices: formatPointList(points),
    },
  });
}

export function buildRectanglePreviewEntity(
  id: string,
  spaceId: string,
  firstCorner: DraftPoint,
  secondCorner: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  // Pass the four corners + `closed: true`. The adapter / preview mesh
  // is responsible for appending the closing edge; we no longer manually
  // duplicate `corners[0]` at the tail of the vertex list. The previous
  // shape (`[...corners, corners[0]]` AND `closed: true`) interacted with
  // the `Closed: "Yes"` serialization bug to produce a 5-vertex polyline
  // with `closed=false` in the renderable — which the draft preview mesh
  // turned into 4 segments by accident, while the batched render path
  // for the committed entity treated it as an open 5-point polyline,
  // dropping the closing edge entirely.
  return buildPolylinePreviewEntity(
    id,
    spaceId,
    rectangleCorners(firstCorner, secondCorner),
    true,
    defaultObjectProperties,
    summary,
  );
}

export function buildMTextPreviewEntity(
  id: string,
  spaceId: string,
  firstCorner: DraftPoint,
  secondCorner: DraftPoint,
  contents: string,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  const width = Math.max(48, Math.abs(secondCorner.x - firstCorner.x));

  return createPreviewEntity({
    id,
    label: t("entity.label.mtext", { id }),
    type: "MText",
    spaceId,
    summary,
    editableProperties: mtextEditableProperties,
    properties: {
      ...defaultObjectProperties,
      Contents: contents,
      "Text style": "STANDARD",
      Height: "24",
      Width: formatNumber(width),
      "Background fill": "Off",
      "Corner 1 X": formatNumber(firstCorner.x),
      "Corner 1 Y": formatNumber(firstCorner.y),
      "Corner 2 X": formatNumber(secondCorner.x),
      "Corner 2 Y": formatNumber(secondCorner.y),
    },
  });
}

export function buildBlockPreviewEntity(
  id: string,
  spaceId: string,
  insertionPoint: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
  blockName = "BLOCK_REF",
  scale = "1",
  rotation = "0",
): EntityPreview {
  return createPreviewEntity({
    id,
    label: t("entity.label.block", { name: blockName, id }),
    type: "Block",
    spaceId,
    summary,
    editableProperties: blockEditableProperties,
    properties: {
      ...defaultObjectProperties,
      Name: blockName,
      "Insertion X": formatNumber(insertionPoint.x),
      "Insertion Y": formatNumber(insertionPoint.y),
      Scale: `${scale},${scale},${scale}`,
      Rotation: rotation,
    },
  });
}

/**
 * Build a preview entity for the ARRAYRECT draft. The source objects
 * themselves are not cloned during preview — instead a `Rectangular
 * Associative Array` placeholder is rendered as crosshair markers at
 * each grid cell. When the command commits, the reducer replaces it
 * with real entity copies.
 */
export function buildArrayRectDraftPreview(
  id: string,
  spaceId: string,
  basePoint: DraftPoint,
  cornerPoint: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
  options: Record<string, string> = {},
): EntityPreview {
  const width = Math.abs(cornerPoint.x - basePoint.x);
  const height = Math.abs(cornerPoint.y - basePoint.y);
  // Defaults mirror AutoCAD legacy: 96x72 cell, min 2 rows/cols
  const columns = Math.max(
    1,
    Number(
      options.arrayColumns ??
        Math.max(2, Math.round(width / DEFAULT_ARRAY_CELL_WIDTH)),
    ),
  );
  const rows = Math.max(
    1,
    Number(
      options.arrayRows ??
        Math.max(2, Math.round(height / DEFAULT_ARRAY_CELL_HEIGHT)),
    ),
  );
  const columnSpacing = Number(
    options.arrayColumnSpacing ??
      (columns > 0 ? Math.max(1, width / columns) : 0),
  );
  const rowSpacing = Number(
    options.arrayRowSpacing ?? (rows > 0 ? Math.max(1, height / rows) : 0),
  );
  const axisAngle = Number(options.arrayAxisAngle ?? 0);

  return createPreviewEntity({
    id,
    label: t("entity.label.arrayrect", { id }),
    type: "Rectangular Associative Array",
    spaceId,
    summary,
    editableProperties: arrayEditableProperties,
    properties: {
      ...defaultObjectProperties,
      Rows: String(rows),
      Columns: String(columns),
      "Row spacing": formatNumber(rowSpacing),
      "Column spacing": formatNumber(columnSpacing),
      "Base X": formatNumber(basePoint.x),
      "Base Y": formatNumber(basePoint.y),
      "Axis angle": formatNumber(axisAngle),
      Associative: "No",
    },
  });
}

export function buildHatchPreviewEntity(
  id: string,
  spaceId: string,
  firstCorner: DraftPoint,
  secondCorner: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
  overrides: {
    pattern?: string;
    scale?: string;
  } = {},
): EntityPreview {
  const corners = rectangleCorners(firstCorner, secondCorner);

  return createPreviewEntity({
    id,
    label: t("entity.label.hatch", { id }),
    type: "Hatch",
    spaceId,
    summary,
    editableProperties: hatchEditableProperties,
    properties: {
      ...defaultObjectProperties,
      Pattern: overrides.pattern ?? "ANSI31",
      Scale: overrides.scale ?? "1",
      Associative: "Yes",
      Boundary: formatPointList(corners),
    },
  });
}

export function buildDimensionPreviewEntity(
  id: string,
  spaceId: string,
  firstPoint: DraftPoint,
  secondPoint: DraftPoint,
  dimensionLinePoint: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  return createPreviewEntity({
    id,
    label: t("entity.label.dimension", { id }),
    type: "Dimension",
    spaceId,
    summary,
    editableProperties: dimensionEditableProperties,
    properties: {
      ...defaultObjectProperties,
      // PR-F P1 — explicit Subtype routes `dimensionToPrimitives`
      // through `emitLinear`. The adapter already defaults the
      // missing key to "Linear" but the explicit key keeps the draft
      // entity's behaviour stable when downstream code consults the
      // property bag directly.
      Subtype: "Linear",
      "Dimension style": "ISO-25",
      Measurement: formatNumber(distanceBetween(firstPoint, secondPoint)),
      "Text override": "<>",
      "Extension 1 X": formatNumber(firstPoint.x),
      "Extension 1 Y": formatNumber(firstPoint.y),
      "Extension 2 X": formatNumber(secondPoint.x),
      "Extension 2 Y": formatNumber(secondPoint.y),
      "Dimension line X": formatNumber((firstPoint.x + secondPoint.x) / 2),
      "Dimension line Y": formatNumber(dimensionLinePoint.y),
    },
  });
}

export function buildMultileaderPreviewEntity(
  id: string,
  spaceId: string,
  arrowPoint: DraftPoint,
  landingPoint: DraftPoint,
  contents: string,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  return createPreviewEntity({
    id,
    label: t("entity.label.mleader", { id }),
    type: "Multileader",
    spaceId,
    summary,
    editableProperties: multileaderEditableProperties,
    properties: {
      ...defaultObjectProperties,
      "Multileader style": "MLEAD_STANDARD",
      Attachment: "Top left",
      "Arrow X": formatNumber(arrowPoint.x),
      "Arrow Y": formatNumber(arrowPoint.y),
      "Landing X": formatNumber(landingPoint.x),
      "Landing Y": formatNumber(landingPoint.y),
      Contents: contents,
    },
  });
}

/**
 * One-truth REVCLOUD preview assembly shared by the rectangular and
 * vertex-path builders below (REVCLOUD-MODES — keeps the corner /
 * style / arc-length rows from being cloned per mode).
 */
function buildRevcloudPreviewCore(
  id: string,
  spaceId: string,
  corner1: DraftPoint,
  corner2: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
  arcLength: number,
  extraProperties: Record<string, string> = {},
): EntityPreview {
  return createPreviewEntity({
    id,
    label: t("entity.label.revcloud", { id }),
    type: "Revision Cloud",
    spaceId,
    summary,
    editableProperties: revisionCloudEditableProperties,
    properties: {
      ...defaultObjectProperties,
      "Cloud style": "Normal",
      "Approximate arc length": formatNumber(arcLength),
      "Corner 1 X": formatNumber(corner1.x),
      "Corner 1 Y": formatNumber(corner1.y),
      "Corner 2 X": formatNumber(corner2.x),
      "Corner 2 Y": formatNumber(corner2.y),
      ...extraProperties,
    },
  });
}

export function buildRevisionCloudPreviewEntity(
  id: string,
  spaceId: string,
  firstCorner: DraftPoint,
  secondCorner: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
  // REVCLOUD-MODES — live arc length from the session (Arc-length
  // keyword); legacy callers keep the historical preview default.
  arcLength = 32,
): EntityPreview {
  return buildRevcloudPreviewCore(
    id,
    spaceId,
    firstCorner,
    secondCorner,
    defaultObjectProperties,
    summary,
    arcLength,
  );
}

/**
 * REVCLOUD-MODES — rubber-band preview for the Polygonal / Freehand
 * vertex-path flows. Emits the `__revcloud_vertices` JSON chain the
 * renderer adapter (`revisionCloudToRenderable`) prefers over the
 * rectangular corner envelope, so the in-draft ghost already traces the
 * picked polygon / sampled freehand path.
 */
export function buildRevisionCloudPathPreviewEntity(
  id: string,
  spaceId: string,
  points: ReadonlyArray<DraftPoint>,
  defaultObjectProperties: Record<string, string>,
  summary: string,
  arcLength: number,
): EntityPreview {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const pt of points) {
    if (pt.x < minX) minX = pt.x;
    if (pt.y < minY) minY = pt.y;
    if (pt.x > maxX) maxX = pt.x;
    if (pt.y > maxY) maxY = pt.y;
  }
  if (minX > maxX) {
    minX = minY = maxX = maxY = 0;
  }
  return buildRevcloudPreviewCore(
    id,
    spaceId,
    { x: minX, y: minY },
    { x: maxX, y: maxY },
    defaultObjectProperties,
    summary,
    arcLength,
    {
      __revcloud_vertices: JSON.stringify(
        points.map((pt) => ({ x: pt.x, y: pt.y })),
      ),
    },
  );
}

// WIPEOUT preview entity — opaque polygon (4 vertices in rectangular
// mode). The renderer picks up the `Vertex N X`/`Vertex N Y` keys and
// fills the polygon with white so users see the wipeout effect during
// the second-corner rubber-band. `Frame` defaults to "On" matching the
// AutoCAD WIPEOUTFRAME = 1 default.
export function buildWipeoutPreviewEntity(
  id: string,
  spaceId: string,
  firstCorner: DraftPoint,
  secondCorner: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  return createPreviewEntity({
    id,
    label: t("entity.label.wipeout", { id }),
    type: "Wipeout",
    spaceId,
    summary,
    editableProperties: wipeoutEditableProperties,
    properties: {
      ...defaultObjectProperties,
      Frame: "On",
      "Vertex 1 X": formatNumber(firstCorner.x),
      "Vertex 1 Y": formatNumber(firstCorner.y),
      "Vertex 2 X": formatNumber(secondCorner.x),
      "Vertex 2 Y": formatNumber(firstCorner.y),
      "Vertex 3 X": formatNumber(secondCorner.x),
      "Vertex 3 Y": formatNumber(secondCorner.y),
      "Vertex 4 X": formatNumber(firstCorner.x),
      "Vertex 4 Y": formatNumber(secondCorner.y),
      "Vertex count": "4",
    },
  });
}

export function buildViewportPreviewEntity(
  id: string,
  spaceId: string,
  firstCorner: DraftPoint,
  secondCorner: DraftPoint,
  defaultObjectProperties: Record<string, string>,
  summary: string,
): EntityPreview {
  return createPreviewEntity({
    id,
    label: t("entity.label.viewport", { id }),
    type: "Viewport",
    spaceId,
    summary,
    editableProperties: viewportEditableProperties,
    properties: {
      Layer: defaultObjectProperties.Layer,
      "Display lock": "Yes",
      Scale: "1:100",
      "Shade plot": "As displayed",
      "Corner 1 X": formatNumber(firstCorner.x),
      "Corner 1 Y": formatNumber(firstCorner.y),
      "Corner 2 X": formatNumber(secondCorner.x),
      "Corner 2 Y": formatNumber(secondCorner.y),
    },
  });
}
